import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Row, Col, Card } from 'react-bootstrap'
import Pagination from '@material-ui/lab/Pagination'
import PropTypes from 'prop-types'
import {
    FormControl,
    Select,
    MenuItem
} from '@material-ui/core'
import './cardGridStyle.scss'
import EmptyState from 'components/ds/EmptyState'
import { dispatchProcessing } from 'actions/root'
import Loader from '../Loader/index'

const pageLimitOptions = [
    { label: `Showing 5 Records`, value: 5 },
    { label: 'Showing 10 Records', value: 10 },
    { label: 'Showing 20 Records', value: 20 },
    { label: 'Showing 40 Records', value: 40 },
    { label: 'Showing 50 Records', value: 50 }
]

const NoDataIndication = () => (
    <div className="no-data-indication">
        <EmptyState compact kind='empty' />
    </div>
)

const CardGrid = (props) => {
    const {
        data,
        cardFields,
        loading,
        isLoading,
        showPagination = true,
        showLimitOptions = true,
        keyField = 'uuid',
        onChangePagination,
        currentPage,
        totalRecords,
        totalPages,
        className,
        paginationLimit,
        useLocalPagination,
        onCardClick,
        renderCard
    } = props

    const presentPage = Number(currentPage)
    const [pagination, setPagination] = useState({
        limit: paginationLimit || 10,
        page: 0,
        totalPages,
        totalRecords
    })
    const { page, limit } = pagination
    const [paginatedData, setPaginatedData] = useState([])
    const dispatch = useDispatch()

    useEffect(() => {
        if (useLocalPagination) {
            const temp = [], max = data.length
            let index = 0
            if (page) index = (page + 1) * limit - limit

            for (; index < (page + 1) * limit; index++) {
                if (data[index])
                    temp.push(data[index])

                if (index === max)
                    break
            }
            setPaginatedData(temp)
            setPagination(prev => ({
                ...prev,
                totalPages: Math.ceil(max / limit),
                totalRecords: max,
            }))
        }
    }, [page, limit, data, useLocalPagination])

    useEffect(() => {
        if (presentPage >= 0)
            setPagination(prev => ({ ...prev, page: presentPage }))
    }, [presentPage])

    useEffect(() => {
        if (typeof loading === 'boolean') {
            dispatch(dispatchProcessing(loading))
        }
    }, [loading, dispatch])

    const handlePagination = (e, value) => {
        let paginationParam = { ...pagination }
        if (e.target.value) {
            paginationParam.limit = e.target.value
            paginationParam.page = 0
        } else {
            paginationParam.page = value - 1
        }
        setPagination({
            ...paginationParam
        })

        if (!useLocalPagination)
            onChangePagination(paginationParam)
    }

    const displayData = useLocalPagination ? paginatedData : data

    return (
        <div className={`card-grid-component ${className}`}>
            {isLoading ? (
                <div className='emptyGrid'><Loader /></div>
            ) : (
                <>
                    {displayData && displayData.length > 0 ? (
                        <div className="card-grid-container">
                            {displayData.map((item, index) => (
                                <div
                                    key={item[keyField] || index}
                                    className="card-col-wrapper"
                                    onClick={() => onCardClick && onCardClick(item)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            onCardClick && onCardClick(item)
                                        }
                                    }}
                                    style={{ cursor: onCardClick ? 'pointer' : 'default' }}
                                >
                                    {renderCard ? (
                                        renderCard(item)
                                    ) : (
                                        <Card
                                            className="data-card"
                                        >
                                            <Card.Body>
                                                {cardFields && cardFields.map((field, idx) => (
                                                    <div key={idx} className="card-field">
                                                        <span className="field-label">{field.label}:</span>
                                                        <span className="field-value">
                                                            {item[field.dataField] || '---'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </Card.Body>
                                        </Card>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-data-container">
                            <NoDataIndication />
                        </div>
                    )}
                </>
            )}

            {showPagination && displayData?.length ? (
                <div className={`pagination-container`}>
                    <div className={`pagination-limit`}>
                        {showLimitOptions && (
                            <>
                                <div>
                                    <FormControl variant='outlined'>
                                        <Select
                                            className={'page-limit-dropdown'}
                                            onChange={handlePagination}
                                            value={pagination.limit}
                                            variant='outlined'
                                        >
                                            {pageLimitOptions.map((ele, index) => (
                                                <MenuItem key={index} value={ele.value}>
                                                    {ele.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                                <p className='total-records'>
                                    {useLocalPagination ? pagination.totalRecords : totalRecords} Total Records
                                </p>
                            </>
                        )}
                    </div>
                    <div className={`pagination-count`}>
                        <Pagination
                            count={Number(useLocalPagination ? pagination.totalPages : totalPages)}
                            onChange={handlePagination}
                            color="primary"
                            page={pagination.page + 1}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    )
}

CardGrid.defaultProps = {
    totalPages: 1,
    onCardClick: null,
    onChangePagination: () => { }
}

CardGrid.propTypes = {
    totalPages: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCardClick: PropTypes.func,
    cardFields: PropTypes.arrayOf(PropTypes.shape({
        dataField: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        formatter: PropTypes.func
    }))
}

export default CardGrid